// Pure helpers for the Deal Research tab (components/shared/DealResearchTab.jsx).
//
// Kept out of the component so they unit-test with no React, no MSAL and no
// network — the same split as utils/docFolders.js and utils/documentIntakeStatus.js.
// Everything here reads what `GET /deals/{id}/research` already returns
// (api/src/functions/dealResearch.js); nothing here calls anything.
//
// Tested in src/__tests__/dealResearch.test.js.

const strip = (s) => String(s ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

// Risks lead: the reader is deciding whether to keep going, and the reasons not
// to are what they are looking for. Drives both the Bottom line ordering and the
// severity filter chips.
export const SENTIMENT_ORDER = ['risk', 'flag', 'positive'];

// Split a brief into titled sections. Primary format is the explicit
// [[SECTION]]Title marker the synthesis emits; falls back to <h2/3/4> headings
// for older briefs.
export function splitBriefSections(html) {
  const src = typeof html === 'string' ? html : '';
  if (!src.trim()) return [];

  if (/\[\[SECTION\]\]/i.test(src)) {
    return src.split(/\[\[SECTION\]\]/i)
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        // Heading = text up to the first newline (or first HTML tag); rest = body.
        const nl = chunk.search(/\r?\n/);
        let head, body;
        if (nl !== -1) { head = chunk.slice(0, nl); body = chunk.slice(nl + 1); }
        else { const lt = chunk.indexOf('<'); head = lt === -1 ? chunk : chunk.slice(0, lt); body = lt === -1 ? '' : chunk.slice(lt); }
        return { heading: strip(head), body: body.trim() };
      });
  }

  // Fallback: split on <h2>/<h3>/<h4> headings.
  const parts = src.split(/<h[234][^>]*>/i);
  const sections = [];
  if (parts[0] && parts[0].trim()) sections.push({ heading: null, body: parts[0].trim() });
  for (let i = 1; i < parts.length; i++) {
    const seg = parts[i];
    const close = seg.search(/<\/h[234]>/i);
    if (close === -1) { sections.push({ heading: strip(seg), body: '' }); continue; }
    sections.push({ heading: strip(seg.slice(0, close)), body: seg.slice(close + 5).trim() });
  }
  return sections.length ? sections : [{ heading: null, body: src }];
}

// Once a bullet's bold lead-in becomes a headline, its remainder has to read as
// a sentence — "Biggest risk" / "utilization assumption is 2.4x…" otherwise
// starts mid-thought. Capitalize the first word of the support.
//
// ONLY an all-lowercase word: this domain is full of words that carry their own
// casing ("kWh/stall/day", "eBay", "iShares"), and "KWh" is a worse error than a
// lowercase start. Skips over leading markup and entities.
export function capitalizeLeadingWord(html) {
  const src = String(html ?? '');
  let inTag = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '<') { inTag = true; continue; }
    if (ch === '>') { inTag = false; continue; }
    if (inTag || /\s/.test(ch)) continue;
    // First non-markup, non-space character — decide on the word it starts.
    const word = (src.slice(i).match(/^[A-Za-z]+/) || [''])[0];
    if (word.length >= 2 && /^[a-z]+$/.test(word)) {
      return src.slice(0, i) + ch.toUpperCase() + src.slice(i + 1);
    }
    return src;
  }
  return src;
}

// The Highlights section is the brief's own "most important takeaways", written
// as a <ul> of one-line bullets — usually `<strong>Lead:</strong> the detail`.
// Rendering it as a raw bullet list wastes it; split each bullet into its claim
// and its support so the claim can carry the type weight.
//
// `detail` stays as HTML (inline <strong>/<em> survive); `lead` is plain text.
export function parseStatements(html) {
  const src = typeof html === 'string' ? html : '';
  if (!src.trim()) return [];

  const fromChunk = (inner) => {
    const trimmed = String(inner ?? '').trim();
    if (!trimmed) return null;
    // A leading <strong>/<b> is the claim; everything after it is the support.
    const bold = trimmed.match(/^\s*<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>\s*/i);
    if (bold) {
      const lead = strip(bold[2]).replace(/[\s:;,.–—-]+$/, '');
      const detail = capitalizeLeadingWord(trimmed.slice(bold[0].length).replace(/^[\s:]+/, ''));
      if (lead) return { lead, detail };
    }
    // No bold lead — take the first sentence as the claim.
    const text = strip(trimmed);
    if (!text) return null;
    const cut = text.search(/[.!?]\s/);
    if (cut > 0 && cut < text.length - 2) {
      return { lead: text.slice(0, cut + 1), detail: text.slice(cut + 2).trim() };
    }
    return { lead: text, detail: '' };
  };

  const collect = (re) => {
    const out = [];
    let m;
    while ((m = re.exec(src))) { const s = fromChunk(m[1]); if (s) out.push(s); }
    return out;
  };

  const li = collect(/<li[^>]*>([\s\S]*?)<\/li>/gi);
  if (li.length) return li;
  const p = collect(/<p[^>]*>([\s\S]*?)<\/p>/gi);
  if (p.length) return p;
  const one = fromChunk(src);
  return one ? [one] : [];
}

const STOP = new Set([
  'the', 'and', 'are', 'but', 'for', 'not', 'its', 'this', 'that', 'with',
  'from', 'per', 'than', 'has', 'have', 'been', 'was', 'were', 'will', 'would', 'can',
  'into', 'over', 'under', 'about', 'they', 'them', 'their', 'these', 'those', 'any',
]);

function tokenize(s) {
  return new Set(
    String(s ?? '')
      .toLowerCase()
      .replace(/<[^>]+>/g, ' ')
      .replace(/[^a-z0-9$%.\s-]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 2 && !STOP.has(w))
  );
}

// A highlight carries no sentiment of its own — the grading lives on the key
// findings, which come from a separate Claude call. Match a highlight back to
// the finding it restates so the claim can carry a severity rail.
//
// CONSERVATIVE ON PURPOSE. Below the threshold — and on a pre-143 schema, where
// there are no key findings at all — a statement renders ungraded rather than
// mis-graded. A wrong colour on the page's lead block is worse than no colour.
export function matchSentiment(text, keyFindings) {
  const a = tokenize(text);
  if (a.size < 3) return null;
  let best = null;
  let bestScore = 0;
  for (const kf of keyFindings || []) {
    if (!kf) continue;
    const b = tokenize(`${kf.lead || ''} ${kf.detail || ''} ${kf.topic || ''}`);
    if (b.size < 3) continue;
    let hit = 0;
    for (const t of a) if (b.has(t)) hit++;
    const score = hit / Math.min(a.size, b.size);
    if (score > bestScore) { bestScore = score; best = kf; }
  }
  return bestScore >= 0.34 && best ? (best.sentiment || null) : null;
}

// Cluster key findings by theme → topic, preserving emitted order within each.
export function groupKeyFindings(items) {
  const groups = [];
  const byGroup = new Map();
  for (const it of (items || [])) {
    if (!it || typeof it !== 'object') continue;
    const gName = String(it.group || 'Findings').trim() || 'Findings';
    let g = byGroup.get(gName);
    if (!g) { g = { name: gName, topics: [], byTopic: new Map() }; byGroup.set(gName, g); groups.push(g); }
    const tName = String(it.topic || '').trim();
    let t = g.byTopic.get(tName || '_');
    if (!t) { t = { name: tName, finds: [] }; g.byTopic.set(tName || '_', t); g.topics.push(t); }
    t.finds.push(it);
  }
  return groups;
}

// An unrecognised sentiment counts as a flag, matching how the finding renders —
// so the tally always sums to the number of findings on screen.
export function tallyFindings(items) {
  const t = { positive: 0, flag: 0, risk: 0, total: 0 };
  for (const it of (items || [])) {
    if (!it || typeof it !== 'object') continue;
    const s = it.sentiment;
    if (s === 'positive' || s === 'flag' || s === 'risk') t[s]++;
    else t.flag++;
    t.total++;
  }
  return t;
}

// Invested / returned / net / MOIC, derived from the cash-flow rows already on
// the response. MOIC is only meaningful once something has actually gone out.
export function cashflowSummary(rows) {
  let invested = 0;
  let returned = 0;
  for (const c of (rows || [])) {
    if (!c || typeof c !== 'object') continue;
    const amt = Math.abs(Number(c.amount) || 0);
    if (c.direction === 'outflow') invested += amt; else returned += amt;
  }
  return { invested, returned, net: returned - invested, moic: invested > 0 ? returned / invested : null };
}

// A raw URL wrapping mid-word reads as an unfinished tool — show the host instead.
export function sourceLabel(url) {
  if (!url || typeof url !== 'string') return null;
  try { return new URL(url).hostname.replace(/^www\./, '') || null; }
  catch { return null; }
}

// What a collapsed section is holding, so a closed row still carries information.
export function sectionPreview(body) {
  const items = (String(body ?? '').match(/<li[^>]*>/gi) || []).length;
  if (items) return `${items} point${items === 1 ? '' : 's'}`;
  const words = strip(body).split(/\s+/).filter(Boolean).length;
  return words ? `${words} word${words === 1 ? '' : 's'}` : null;
}
